import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Instagram, Youtube } from "lucide-react";
import { API } from "@/lib/site";
import { Link } from "react-router-dom";

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/templebethelwpbfl", Icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/templebethelwpb/", Icon: Instagram },
  { label: "YouTube", href: "https://www.youtube.com/@templebethel-westpalmbeach", Icon: Youtube },
];

const NAV = [
  { label: "Our Story", href: "/story" },
  { label: "Community", href: "/groups" },
  { label: "Worship", href: "/#worship" },
  { label: "Life Cycle", href: "/contact#lifecycle" },
  { label: "Events", href: "/#events" },
  { label: "Memories", href: "/memories" },
  { label: "News", href: "/news" },
  { label: "Contact", href: "/contact" },
  { label: "Join Us", href: "/#join" },
];

const AFFILIATES = [
  { label: "USCJ", href: "https://www.uscj.org" },
  { label: "Jewish Federation of Palm Beach County", href: "https://www.jewishpb.org" },
  { label: "Mandel JCC", href: "https://www.jcconline.com" },
  { label: "Alpert Jewish Family Service", href: "https://www.alpertjfs.org" },
];

function NewsletterBox() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, form);
      setDone(true);
      toast.success("You're on the list!");
    } catch {
      toast.error("Something went wrong", { description: "Please try again." });
    }
  };

  if (done) {
    return (
      <p className="mt-8 text-sm text-gold" data-testid="newsletter-success">
        Welcome to the family — check your inbox.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 space-y-3" data-testid="newsletter-form">
      <p className="text-[10px] tracking-[0.3em] uppercase text-foreground/40">Join our mailing list</p>
      <input
        data-testid="newsletter-name-input"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Your name"
        className="w-full bg-transparent border-b border-white/15 py-2.5 text-sm placeholder:text-foreground/30 focus:outline-none focus:border-gold transition-colors"
      />
      <div className="flex gap-3">
        <input
          data-testid="newsletter-email-input"
          required
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email address"
          className="flex-1 bg-transparent border-b border-white/15 py-2.5 text-sm placeholder:text-foreground/30 focus:outline-none focus:border-gold transition-colors"
        />
        <button
          data-testid="newsletter-submit-button"
          type="submit"
          className="px-5 py-2.5 bg-gold text-inkdeep text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors"
        >
          Subscribe
        </button>
      </div>
    </form>
  );
}

export default function Footer() {
  return (
    <footer data-testid="footer" className="border-t border-white/10 pt-20 pb-10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img src="/img/firstparty/1761063128.webp" alt="Beth El Center for Jewish Life" loading="lazy" decoding="async" className="h-10 w-10 object-contain" />
              <span className="font-syne font-bold tracking-tight">BETH EL CENTER FOR JEWISH LIFE</span>
            </div>
            <p className="font-cormorant italic text-lg text-foreground/60 max-w-sm">
              Beth El Center for Jewish Life — a century of light, faith & community in West Palm Beach. The next hundred years start with you.
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Beth El on ${label}`}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-10 h-10 flex items-center justify-center border border-white/15 text-foreground/60 hover:text-gold hover:border-gold transition-colors"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
            <NewsletterBox />
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] tracking-[0.3em] uppercase text-foreground/40 mb-5">Explore</p>
            <nav className="flex flex-col gap-3">
              <a
                data-testid="footer-link-bulletins"
                href="/bulletins"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                Bulletin Archive
              </a>
              <a
                data-testid="footer-link-videos"
                href="/videos"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                Video Corner
              </a>
              <a
                data-testid="footer-link-kids"
                href="/kids"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                Kids &amp; Families
              </a>
              <a
                data-testid="footer-link-schmooze"
                href="/schmooze"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                The Beth El Schmooze
              </a>
              <a
                data-testid="footer-link-calendar"
                href="/calendar"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                Community Calendar
              </a>
              <a
                data-testid="footer-link-account"
                href="https://templebethelwpb.shulcloud.com/member/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                My Account
              </a>
              <a
                data-testid="footer-link-privacy"
                href="/privacy"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                Privacy Policy
              </a>
              <a
                data-testid="footer-link-directory"
                href="/members"
                className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
              >
                Member Login &amp; Directory
              </a>
              {NAV.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-left text-sm text-foreground/70 hover:text-gold transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="md:col-span-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-foreground/40 mb-5">Our Affiliations</p>
            <div className="flex flex-col gap-3">
              {AFFILIATES.map((a) => (
                <a
                  key={a.label}
                  data-testid={`footer-affiliate-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  href={a.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-foreground/70 hover:text-gold transition-colors"
                >
                  {a.label}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-foreground/40">© 2026 Beth El Center for Jewish Life · West Palm Beach.</p>
          <p className="font-cormorant italic text-sm text-gold/70">Shabbat Shalom · שבת שלום</p>
        </div>
      </div>
    </footer>
  );
}
